/**
 * 【商城活动】付款方式 / 赠品类型的前端口径 —— REQ-2026-826259
 *
 * 枚举名与后端 com.du.admin.service.activity.rule.ActivityPayMode、
 * goods_activity_condition.reward_type 严格一致，**禁止改名**，
 * 改这里必须同步改 admin-api 与 app-server 两个工程的镜像枚举。
 *
 * 注意 activityModules 与 payModes 是两个字段、两个维度：
 *   activityModules 决定活动出现在哪个商城（1余额商城 2零售价商城 3赠送额度商城）
 *   payModes        决定哪种付款方式能触发这条活动规则（CASH/BALANCE/XIAODUBI/GIFT）
 * 余额商城里现金单和余额单的 activityModules 都是 1，只有 payModes 分得开。
 */

/** 付款方式（对应 goods_activity.pay_modes，逗号分隔） */
export const PAY_MODE_OPTIONS = [
  { key: 'CASH', value: '现金' },
  { key: 'BALANCE', value: '余额' },
  { key: 'XIAODUBI', value: '零售价' },
  { key: 'GIFT', value: '学费赠送' }
]

/** 赠品类型（对应 goods_activity_condition.reward_type，NOT NULL，默认 GOODS） */
export const REWARD_TYPE_GOODS = 'GOODS'

export const REWARD_TYPE_OPTIONS = [
  { key: 'GOODS', value: '实物赠品' },
  { key: 'BALANCE', value: '余额' },
  { key: 'XIAODUBI', value: '零售价' },
  { key: 'GIFT', value: '学费赠送' }
]

/** 赠送数额的单位，仅虚拟币赠品用得上 */
const REWARD_UNIT = {
  BALANCE: '元',
  XIAODUBI: '零售价',
  GIFT: '元'
}

/**
 * 活动模块 → 付款方式 的老口径映射。
 *
 * 与后端 ActivityPayModeMatcher.fallbackFromActivityModules 及迁移脚本第二段严格一致：
 * 含 1 → 余额 + **现金**；含 2 → 零售价；含 3 → 学费赠送。
 * 漏掉 CASH 等于把所有现金单的赠品砍掉。
 */
export const MODULE_PAY_MODES = {
  1: ['BALANCE', 'CASH'],
  2: ['XIAODUBI'],
  3: ['GIFT']
}

/** 商城模块名，报错文案用 */
export const MODULE_NAMES = {
  1: '余额商城',
  2: '零售价商城',
  3: '赠送额度商城'
}

const isKnownModule = key => Object.prototype.hasOwnProperty.call(MODULE_PAY_MODES, key)

/** csv -> 数组，认不出的值直接丢掉，与后端 parseCsv 一致 */
export function parsePayModes(csv) {
  if (!csv) return []
  const known = PAY_MODE_OPTIONS.map(item => item.key)
  return String(csv)
    .split(',')
    .map(item => item.trim().toUpperCase())
    .filter(item => known.indexOf(item) > -1)
}

/** activityModules(csv 或数组) -> 付款方式数组，前端的兜底推导 */
export function fallbackPayModes(activityModules) {
  if (!activityModules) return []
  const modules = Array.isArray(activityModules)
    ? activityModules
    : String(activityModules).split(',')
  const result = []
  modules.forEach(item => {
    const key = String(item).trim()
    const mapped = isKnownModule(key) ? MODULE_PAY_MODES[key] : []
    mapped.forEach(mode => {
      if (result.indexOf(mode) === -1) result.push(mode)
    })
  })
  // 按 PAY_MODE_OPTIONS 的顺序输出，保证与后端 toCsv 的顺序一致
  return PAY_MODE_OPTIONS.map(item => item.key).filter(key => result.indexOf(key) > -1)
}

function selectionValues(value) {
  if (value == null) return []
  return (Array.isArray(value) ? value : String(value).split(','))
    .map(item => String(item).trim()).filter(Boolean)
}

/** 编辑仅为空付款方式回显旧口径；已有合法子集不被补全。 */
export function initialActivitySelection(activityModules, payModes) {
  const modules = selectionValues(activityModules).filter(isKnownModule)
  const legacyEmpty = payModes == null || !String(payModes).trim()
  return { modules, payModes: legacyEmpty ? fallbackPayModes(modules) : parsePayModes(selectionValues(payModes).join(',')) }
}

/** 只给新勾选商城补默认付款方式；取消商城时清除它独占的方式。 */
export function toggleActivityModule(activityModules, payModes, moduleKey) {
  const modules = selectionValues(activityModules)
  const key = String(moduleKey)
  if (!isKnownModule(key)) return { modules, payModes: parsePayModes(selectionValues(payModes).join(',')) }
  const adding = modules.indexOf(key) === -1
  const nextModules = adding ? modules.concat(key) : modules.filter(item => item !== key)
  const allowed = fallbackPayModes(nextModules)
  const modes = selectionValues(payModes).concat(adding ? MODULE_PAY_MODES[key] : [])
  return { modules: nextModules, payModes: allowed.filter(mode => modes.indexOf(mode) > -1) }
}

/** CASH/BALANCE可分别取消；某商城最后一种方式取消后才取消该商城。 */
export function toggleActivityPayMode(activityModules, payModes, modeKey) {
  const modules = selectionValues(activityModules)
  const modes = parsePayModes(selectionValues(payModes).join(','))
  if (fallbackPayModes(modules).indexOf(modeKey) === -1) return { modules, payModes: modes }
  const nextModes = modes.indexOf(modeKey) > -1 ? modes.filter(mode => mode !== modeKey) : modes.concat(modeKey)
  return {
    modules: modules.filter(key => isKnownModule(key) && MODULE_PAY_MODES[key].some(mode => nextModes.indexOf(mode) > -1)),
    payModes: PAY_MODE_OPTIONS.map(item => item.key).filter(mode => nextModes.indexOf(mode) > -1)
  }
}

/** 新增/编辑显式提交，空值不能再走旧数据回落。文案与后台校验一致。 */
export function activitySelectionErrors(activityModules, payModes) {
  const modules = selectionValues(activityModules)
  const modes = selectionValues(payModes)
  const errors = {}
  if (!modules.length) errors.activityModules = '请选择参与活动条件'
  else if (modules.some(key => !isKnownModule(key))) errors.activityModules = '参与活动条件无效，请重新选择'
  if (!modes.length) errors.payModes = '请选择参与付款方式'
  else if (modes.some(mode => !PAY_MODE_OPTIONS.some(item => item.key === mode))) errors.payModes = '参与付款方式无效，请重新选择'
  else if (!errors.activityModules) {
    const allowed = fallbackPayModes(modules)
    if (modes.some(mode => allowed.indexOf(mode) === -1)) errors.payModes = '参与付款方式超出所选参与活动条件，请重新选择'
    else if (modules.some(key => !MODULE_PAY_MODES[key].some(mode => modes.indexOf(mode) > -1))) {
      errors.payModes = '每个参与活动条件至少选择一种对应付款方式'
    }
  }
  return errors
}

/**
 * 付款方式中文，多个用顿号分隔。
 * payModes 为空时按 activityModules 推导（存量活动落库前是空的，判定时后端也是这么回落的）。
 */
export function payModesText(payModes, activityModules) {
  let keys = parsePayModes(payModes)
  if (keys.length === 0) keys = fallbackPayModes(activityModules)
  if (keys.length === 0) return ''
  return keys
    .map(key => {
      const find = PAY_MODE_OPTIONS.find(item => item.key === key)
      return find ? find.value : key
    })
    .join('、')
}

/** 该活动的付款方式是不是推导出来的（列表页要给运营一个提示） */
export function isPayModesDerived(payModes) {
  return parsePayModes(payModes).length === 0
}

/** 空 / GOODS 一律按实物，与后端 ActivityRewardService.isVirtualReward 一致 */
export function isVirtualReward(rewardType) {
  const key = rewardType ? String(rewardType).trim().toUpperCase() : ''
  return !!key && key !== REWARD_TYPE_GOODS
}

export function rewardTypeText(rewardType) {
  const key = rewardType ? String(rewardType).trim().toUpperCase() : REWARD_TYPE_GOODS
  const find = REWARD_TYPE_OPTIONS.find(item => item.key === key)
  return find ? find.value : key
}

export function rewardUnit(rewardType) {
  const key = rewardType ? String(rewardType).trim().toUpperCase() : ''
  return REWARD_UNIT[key] || ''
}

/**
 * 一条活动条件的赠送内容文案，列表页与详情页共用。
 * activityType: 1满件赠 2满金额赠
 */
export function rewardText(condition) {
  if (!condition) return ''
  if (isVirtualReward(condition.rewardType)) {
    return `${condition.rewardValue || 0}${rewardUnit(condition.rewardType)}${rewardTypeText(condition.rewardType)}`
  }
  return `${condition.giftNum || 0}件赠品`
}

// fullNum 在现有 DTO/数据库中为 Integer，满件与满金额均保留整数门槛。
export function activityThresholdError(value, activityType) {
  const text = value == null ? '' : String(value).trim()
  if (!text) return '请填写满赠门槛'
  const number = Number(text)
  if ((typeof value !== 'string' && typeof value !== 'number') ||
      !/^\d+$/.test(text) || !Number.isSafeInteger(number) || number < 1 || number > 2147483647) {
    return Number(activityType) === 2
      ? '满金额门槛须为1至2147483647的整数金额'
      : '满件门槛须为1至2147483647的正整数'
  }
  return null
}
